<template>
  <b-modal
    :id="modalId"
    size="lg"
    :title="$t('integration.sync_now', 'Sync now')"
    :hide-footer="true"
    @shown="onShown"
    @hidden="onHidden"
  >
    <div v-if="!isEnabled" class="sync-disabled">
      <b-alert variant="warning" show>
        {{
          $t(
            "integration.sync_disabled",
            "This integration is switched off. Enable it first, then run a sync."
          )
        }}
      </b-alert>
      <button class="bttn bttn--lg bttn--orange" @click.prevent="enableIntegration">
        {{ $t("integration.enable", "Enable") }}
      </button>
    </div>

    <template v-else>
      <div v-if="!sessionId" class="sync-start">
        <b-form-group
          :label="$t('integration.sync_campaign', 'Campaign (optional)')"
          :description="
            $t(
              'integration.sync_campaign_hint',
              'Leave empty to cover every campaign the scheduled export would.'
            )
          "
          :invalid-feedback="campaignError"
          :state="campaignError ? false : null"
        >
          <multiselect
            v-model="selectedCampaign"
            :options="campaignOptions"
            :allow-empty="true"
            track-by="id"
            :custom-label="(c) => `[${c.id}] ${c.title}`"
            :placeholder="$t('integration.sync_campaign_placeholder', 'All campaigns')"
          />
        </b-form-group>

        <button class="bttn bttn--lg bttn--orange" :disabled="starting" @click.prevent="startSync">
          <b-spinner v-if="starting" small class="mr-1" />
          {{ $t("integration.sync_now", "Sync now") }}
        </button>
      </div>

      <div v-else class="sync-run">
        <b-alert v-if="attached" variant="info" show>
          {{
            $t(
              "integration.sync_already_running",
              "A sync is already running for this organization. Showing that run."
            )
          }}
        </b-alert>

        <b-alert v-else-if="startedNotice" variant="info" show>
          {{ startedNotice }}
        </b-alert>

        <b-alert v-if="lostContact" variant="danger" show>
          {{
            $t(
              "integration.sync_lost_contact",
              "Lost contact with this run. Reopen to check again."
            )
          }}
          <button class="btn btn-link p-0 align-baseline" @click.prevent="retryPolling">
            {{ $t("integration.sync_retry", "Try again") }}
          </button>
        </b-alert>

        <div class="sync-run__status">
          <template v-if="run && run.finished">
            {{
              $t(
                "integration.sync_finished",
                "Sync finished — {exported} exported, {failed} failed",
                {
                  exported: run.exported_count || 0,
                  failed: run.failed_count || 0,
                }
              )
            }}
          </template>
          <template v-else>
            {{ $t("integration.sync_in_progress", "Syncing…") }}
          </template>
        </div>

        <b-progress
          class="sync-run__progress"
          :value="run ? run.progress || 0 : 0"
          :max="100"
          :variant="run && run.finished ? 'success' : 'primary'"
          show-progress
        />

        <b-alert v-if="run && run.truncated" variant="warning" show>
          {{ $t("integration.sync_truncated", "Showing the first 500 lines of this run.") }}
        </b-alert>

        <div
          v-for="campaign in runCampaigns"
          :key="campaign.campaign_id"
          class="sync-run__campaign"
        >
          <div class="sync-run__campaign-title">
            {{
              $t("integration.sync_campaign_title", "Campaign {id}", { id: campaign.campaign_id })
            }}
            <b-badge :variant="campaign.finished ? 'success' : 'secondary'">
              {{
                campaign.finished
                  ? $t("integration.sync_campaign_done", "Finished")
                  : $t("integration.sync_campaign_running", "Running")
              }}
            </b-badge>
          </div>

          <ul v-if="campaign.logs && campaign.logs.length" class="sync-run__lines">
            <li v-for="(line, index) in campaign.logs" :key="`log-${index}`">{{ line }}</li>
          </ul>

          <ul
            v-if="campaign.errors && campaign.errors.length"
            class="sync-run__lines sync-run__lines--error"
          >
            <li v-for="(line, index) in campaign.errors" :key="`err-${index}`">{{ line }}</li>
          </ul>
        </div>
      </div>
    </template>
  </b-modal>
</template>

<script>
import { mapState } from "vuex";
import Multiselect from "vue-multiselect";

const POLL_INTERVAL_MS = 2000;
const MAX_FAILED_POLLS = 5;
const MAX_POLL_DURATION_MS = 15 * 60 * 1000;

export default {
  name: "IntegrationSync",

  components: {
    Multiselect,
  },

  props: {
    integration: {
      type: Object,
      required: true,
    },
    modalId: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      starting: false,
      attached: false,
      startedNotice: "",
      campaignError: "",
      selectedCampaign: null,
      // Local copy of the run being watched. Kept separately from the store
      // entry so a finished run stays on screen after the store entry is
      // cleared.
      sessionId: null,
      run: null,
      lostContact: false,
      failedPolls: 0,
      pollingStartedAt: 0,
      interval: null,
    };
  },

  computed: {
    ...mapState({
      campaigns: (state) => state.campaigns.campaigns,
      activeSyncRuns: (state) => state.integrations.activeSyncRuns,
    }),

    isEnabled() {
      return this.integration.attributes.status === 1;
    },

    procSessionId() {
      return this.activeSyncRuns[String(this.integration.id)] || null;
    },

    campaignOptions() {
      return this.campaigns.map((c) => ({ id: Number(c.id), title: c.attributes.title }));
    },

    runCampaigns() {
      return this.run?.campaigns || [];
    },
  },

  // The precedent in requestReceiptModal only clears its interval on
  // completion; a modal torn down mid-run would otherwise poll forever.
  beforeDestroy() {
    this.stopPolling();
  },

  methods: {
    onShown() {
      this.$store.dispatch("getCampaigns");

      // Reattach to a run started before the modal was last closed.
      if (this.procSessionId) {
        this.sessionId = this.procSessionId;
        this.startPolling();
      }
    },

    onHidden() {
      this.stopPolling();
      this.startedNotice = "";
      this.attached = false;
      this.campaignError = "";
      this.sessionId = null;
      this.run = null;
      this.lostContact = false;
    },

    enableIntegration() {
      this.$emit("enable-integration");
    },

    startSync() {
      this.starting = true;
      this.campaignError = "";
      this.run = null;
      this.lostContact = false;

      this.$store
        .dispatch("startIntegrationSync", {
          integrationId: this.integration.id,
          campaignId: this.selectedCampaign?.id,
        })
        .then(({ attached, run }) => {
          this.attached = attached;
          this.sessionId = run.proc_session_id;

          if (!attached) {
            this.startedNotice = this.$t(
              "integration.sync_started",
              "Sync started — covering {count} campaign(s)",
              { count: (run.campaign_ids || []).length }
            );
          }

          this.startPolling();
        })
        .catch((error) => {
          const errors = error?.response?.data?.errors || [];
          const notEligible = errors.find((e) => e?.code === "campaign_not_eligible");

          if (notEligible) {
            this.campaignError =
              notEligible.detail ||
              this.$t(
                "integration.sync_campaign_not_eligible",
                "This integration does not export that campaign."
              );
            return;
          }

          this.$_notificationsMixin_handleCatch(error);
        })
        .finally(() => {
          this.starting = false;
        });
    },

    startPolling() {
      this.stopPolling();
      this.failedPolls = 0;
      this.lostContact = false;
      this.pollingStartedAt = Date.now();
      this.pollOnce();
      this.interval = setInterval(this.pollOnce, POLL_INTERVAL_MS);
    },

    stopPolling() {
      if (this.interval) {
        clearInterval(this.interval);
        this.interval = null;
      }
    },

    retryPolling() {
      this.startPolling();
    },

    pollOnce() {
      // A run interrupted server-side is reaped by a TTL; do not wait on it
      // forever.
      if (this.interval && Date.now() - this.pollingStartedAt > MAX_POLL_DURATION_MS) {
        this.stopPolling();
        this.lostContact = true;
        return;
      }

      const procSessionId = this.sessionId;

      if (!procSessionId) {
        this.stopPolling();
        return;
      }

      this.$store
        .dispatch("fetchIntegrationSyncStatus", {
          integrationId: this.integration.id,
          procSessionId,
        })
        .then((run) => {
          this.failedPolls = 0;
          this.run = run;

          if (run.finished) {
            this.stopPolling();
            this.$store.commit("clearActiveSyncRun", this.integration.id);
            this.$store.dispatch("getIntegrationsList");
          }
        })
        .catch(() => {
          this.failedPolls += 1;

          if (this.failedPolls >= MAX_FAILED_POLLS) {
            this.stopPolling();
            this.lostContact = true;
          }
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.sync-run__status {
  font-size: 16px;
  margin-bottom: 10px;
}

.sync-run__progress {
  margin-bottom: 15px;
}

.sync-run__campaign {
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid #e6eaed;

  &:last-of-type {
    border-bottom: none;
  }

  &-title {
    font-weight: 600;
    margin-bottom: 5px;
  }
}

.sync-run__lines {
  margin: 0;
  padding-left: 18px;
  font-size: 14px;
  word-break: break-word;

  &--error {
    color: #dc3545;
  }
}
</style>
